// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { Icon, type IconName, type IconProps, Spinner } from '@plesk/ui-library';
import type { ReactNode } from 'react';
import type { BatchState } from './toolCalls';

// The outlined circle of a pending call; the icon sprite has no plain ring
const PendingRing = () => (
    <svg viewBox="0 0 16 16" focusable="false">
        <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zM2 8a6 6 0 1 1 12 0A6 6 0 0 1 2 8z" />
    </svg>
);

const ICONS: Record<Exclude<BatchState, 'running' | 'pending'>, IconName> = {
    queued: 'pause-circle',
    succeeded: 'check-mark',
    'failed-notfound': 'minus-circle',
    'failed-error': 'triangle-exclamation-mark',
    rejected: 'cross-mark',
    mixed: 'dot',
};

const INTENTS: Record<Exclude<BatchState, 'running'>, IconProps['intent']> = {
    pending: 'warning',
    queued: 'inactive',
    succeeded: 'success',
    'failed-notfound': 'inactive',
    'failed-error': 'danger',
    rejected: 'inactive',
    mixed: 'inactive',
};

type ToolCallGlyphProps = {
    state: BatchState;
    // The state name, as the glyph's accessible name; none makes the glyph decorative
    label?: string;
    variant: 'row' | 'batch';
};

const ToolCallGlyph = ({ state, label, variant }: ToolCallGlyphProps) => {
    let glyph: ReactNode;
    if (state === 'running') {
        glyph = <Spinner colorAnimation={false} />;
    } else if (state === 'pending') {
        glyph = <Icon intent={INTENTS[state]}><PendingRing /></Icon>;
    } else {
        glyph = <Icon name={ICONS[state]} intent={INTENTS[state]} />;
    }

    return (
        <span
            className={`plesk-copilot-tool-glyph plesk-copilot-tool-glyph--${variant} plesk-copilot-tool-glyph--${state}`}
            {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
            data-state={state}
        >
            {glyph}
        </span>
    );
};

export default ToolCallGlyph;
